import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FolderArchive, Upload, Download, Trash2, FilePlus2, Info, Sparkles, Loader2, History } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Callout } from '@/components/ui/callout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { documentsApi, assessmentsApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { useEntitlements } from '@/hooks/use-entitlements'
import type { SchoolDocument, DocumentType } from '@/types'

const TIPI_DOCUMENTO: DocumentType[] = ['DPA', 'DELIBERA', 'VERBALE', 'INFORMATIVA', 'NOMINA', 'ALTRO']

const UPLOAD_ROLES = ['DPO', 'DIRIGENTE', 'SEGRETERIA']
const DELETE_ROLES = ['DPO', 'DIRIGENTE']

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function Documenti() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'it' ? 'it-IT' : 'en-GB'
  const { user } = useAuthStore()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { data: ent, isLoading: entLoading } = useEntitlements()
  const isPaid = !!ent && ent.plan !== 'FREE'
  const canUpload = !!user && UPLOAD_ROLES.includes(user.role)
  const canDelete = !!user && DELETE_ROLES.includes(user.role)

  const [uploadOpen, setUploadOpen] = useState(false)
  const [versionTarget, setVersionTarget] = useState<SchoolDocument | null>(null)
  const [historyGroup, setHistoryGroup] = useState<SchoolDocument | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [docType, setDocType] = useState<DocumentType>('DPA')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [appId, setAppId] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: documents, isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: () => documentsApi.list(),
    enabled: isPaid,
  })

  const { data: registry } = useQuery({
    queryKey: ['registry'],
    queryFn: assessmentsApi.registry,
    enabled: isPaid && canUpload,
  })

  const { data: versions } = useQuery({
    queryKey: ['documents', 'group', historyGroup?.group_id],
    queryFn: () => documentsApi.list({ group_id: historyGroup!.group_id }),
    enabled: !!historyGroup,
  })

  const resetForm = () => {
    setFile(null)
    setDocType('DPA')
    setTitle('')
    setDescription('')
    setAppId('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['documents'] })

  const uploadMutation = useMutation({
    mutationFn: () =>
      documentsApi.upload({
        file: file!,
        doc_type: docType,
        title,
        description: description || undefined,
        app_id: appId || undefined,
      }),
    onSuccess: () => {
      invalidate()
      toast({ title: t('documenti.toastUploaded') })
      setUploadOpen(false)
      resetForm()
    },
    onError: (err: any) =>
      toast({
        title: t('documenti.toastUploadKo'),
        description: err?.response?.data?.detail ?? t('common.unexpectedError'),
        variant: 'destructive',
      }),
  })

  const versionMutation = useMutation({
    mutationFn: () =>
      documentsApi.newVersion(versionTarget!.id, file!, description || undefined),
    onSuccess: () => {
      invalidate()
      toast({ title: t('documenti.toastVersionUploaded') })
      setVersionTarget(null)
      resetForm()
    },
    onError: (err: any) =>
      toast({
        title: t('documenti.toastUploadKo'),
        description: err?.response?.data?.detail ?? t('common.unexpectedError'),
        variant: 'destructive',
      }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => documentsApi.delete(id),
    onSuccess: () => {
      invalidate()
      toast({ title: t('documenti.toastDeleted') })
    },
    onError: (err: any) =>
      toast({
        title: t('documenti.toastDeleteKo'),
        description: err?.response?.data?.detail ?? t('common.unexpectedError'),
        variant: 'destructive',
      }),
  })

  const rows = documents ?? []

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="it-page-title">
          <p className="text-[11px] font-bold uppercase tracking-wider text-mim-blue dark:text-primary mb-1">
            {t('documenti.eyebrow')}
          </p>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderArchive className="w-6 h-6 text-mim-blue dark:text-primary" />
            {t('documenti.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('documenti.subtitle')}
          </p>
        </div>
        {isPaid && canUpload && (
          <Button onClick={() => setUploadOpen(true)} className="flex-shrink-0">
            <Upload className="w-4 h-4 mr-2" />
            {t('documenti.upload')}
          </Button>
        )}
      </div>

      {entLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : !isPaid ? (
        <Callout variant="note" title={t('documenti.freeTitle')} icon={<Sparkles className="w-4 h-4" />}>
          {t('documenti.freeBody')}
        </Callout>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('documenti.tableTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Info className="w-8 h-8 mx-auto mb-3 opacity-40" />
                <p className="text-sm">{t('documenti.emptyTitle')}</p>
                {canUpload && (
                  <p className="text-xs mt-1">
                    {t('documenti.emptyHint')}
                  </p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {[
                        t('documenti.headers.documento'),
                        t('documenti.headers.tipo'),
                        t('documenti.headers.strumento'),
                        t('documenti.headers.versione'),
                        t('documenti.headers.caricato'),
                        t('documenti.headers.integrita'),
                        t('documenti.headers.azioni'),
                      ].map((h) => (
                        <th key={h} className="text-left py-2 pr-4 font-semibold text-muted-foreground text-xs uppercase">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d.id} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-3 pr-4">
                          <p className="font-medium">{d.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {d.filename} · {formatSize(d.size_bytes)}
                          </p>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant="outline" className="text-xs">{t(`documenti.tipo.${d.doc_type}` as any)}</Badge>
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground">{d.app_name || '—'}</td>
                        <td className="py-3 pr-4 text-muted-foreground">
                          v{d.version}
                          {d.versions_count > 1 && (
                            <button
                              className="ml-1 inline-flex items-center text-mim-blue dark:text-primary hover:underline text-xs"
                              onClick={() => setHistoryGroup(d)}
                            >
                              <History className="w-3 h-3 mr-0.5" />
                              {t('documenti.historyLink')}
                            </button>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-xs text-muted-foreground">
                          {new Date(d.uploaded_at).toLocaleDateString(locale)}
                          {d.uploaded_by_name && <><br />{d.uploaded_by_name}</>}
                        </td>
                        <td className="py-3 pr-4">
                          <code className="text-[10px] text-muted-foreground" title={d.sha256}>
                            {d.sha256.slice(0, 12)}…
                          </code>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="h-7 px-2" asChild title={t('documenti.downloadTitle')}>
                              <a href={documentsApi.downloadUrl(d.id)} download>
                                <Download className="w-3.5 h-3.5" />
                              </a>
                            </Button>
                            {canUpload && (
                              <Button
                                variant="ghost" size="sm" className="h-7 px-2"
                                title={t('documenti.newVersionTitle')}
                                onClick={() => { resetForm(); setVersionTarget(d) }}
                              >
                                <FilePlus2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost" size="sm" className="h-7 px-2 text-rosso hover:text-rosso-dark"
                                title={t('documenti.deleteTitle')}
                                onClick={() => {
                                  if (window.confirm(t('documenti.deleteConfirm', { title: d.title, version: d.version }))) {
                                    deleteMutation.mutate(d.id)
                                  }
                                }}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload / new-version dialog */}
      <Dialog
        open={uploadOpen || !!versionTarget}
        onOpenChange={(open) => {
          if (!open) { setUploadOpen(false); setVersionTarget(null); resetForm() }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {versionTarget
                ? t('documenti.dialog.newVersionTitle', { title: versionTarget.title, version: versionTarget.version + 1 })
                : t('documenti.dialog.uploadTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('documenti.dialog.fileLabel')}</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.odt,.txt,.png,.jpg,.jpeg,.xml,.p7m,.csv,.xlsx,.ods"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
            {!versionTarget && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('documenti.dialog.typeLabel')}</Label>
                    <Select value={docType} onValueChange={(v) => setDocType(v as DocumentType)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPI_DOCUMENTO.map((k) => (
                          <SelectItem key={k} value={k}>{t(`documenti.tipo.${k}` as any)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('documenti.dialog.linkedLabel')}</Label>
                    <Select value={appId || 'none'} onValueChange={(v) => setAppId(v === 'none' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder={t('documenti.dialog.linkedNone')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('documenti.dialog.linkedNone')}</SelectItem>
                        {(registry ?? []).map((e) => (
                          e.app_id ? <SelectItem key={e.app_id} value={e.app_id}>{e.strumento}</SelectItem> : null
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('documenti.dialog.titleLabel')}</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('documenti.dialog.titlePlaceholder')}
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>{t('documenti.dialog.descLabel')}</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadOpen(false); setVersionTarget(null); resetForm() }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => (versionTarget ? versionMutation.mutate() : uploadMutation.mutate())}
              disabled={!file || (!versionTarget && title.trim().length < 3)
                || uploadMutation.isPending || versionMutation.isPending}
            >
              {(uploadMutation.isPending || versionMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {t('documenti.dialog.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version history dialog */}
      <Dialog open={!!historyGroup} onOpenChange={(open) => !open && setHistoryGroup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('documenti.versionsTitle', { title: historyGroup?.title ?? '' })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {(versions ?? []).map((v) => (
              <div key={v.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <div>
                  <p className="font-medium">v{v.version} · {v.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(v.uploaded_at).toLocaleString(locale)}
                    {v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ''} ·{' '}
                    <code title={v.sha256}>{v.sha256.slice(0, 12)}…</code>
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="h-7 px-2" asChild>
                  <a href={documentsApi.downloadUrl(v.id)} download>
                    <Download className="w-3.5 h-3.5" />
                  </a>
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
